<template>
  <section class="product-showcase" id="products">
    <div class="container">
      <div class="products-grid">
        <div
          class="product-card"
          v-for="product in products"
          :key="product.id"
          :style="{ backgroundImage: `url(${product.image})` }"
        >
          <div class="card-overlay"></div>
          <div class="card-content">
            <div class="product-icon" v-html="product.icon"></div>
            <h3 class="product-title">{{ t(product.titleKey) }}</h3>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface Product {
  id: number
  titleKey: string
  icon: string
  image: string
}

const { t } = useI18n()

const products = ref<Product[]>([
  {
    id: 1,
    titleKey: 'home.products.actuators',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="M15 3v18"/></svg>',
    image: new URL('@/assets/products-center/Penumatic Actuators.webp', import.meta.url).href
  },
  {
    id: 2,
    titleKey: 'home.products.controllers',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M9 9h.01"/><path d="M15 9h.01"/><path d="M9 15h6"/></svg>',
    image: new URL('@/assets/products-center/Pneumatic Controllers.png', import.meta.url).href
  },
  {
    id: 3,
    titleKey: 'home.products.accessories',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/></svg>',
    image: new URL('@/assets/products-center/Pneumatic Accessories.png', import.meta.url).href
  },
  {
    id: 4,
    titleKey: 'home.products.emotion',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3v4"/><path d="M8 3v4"/><path d="M2 11h20"/></svg>',
    image: new URL('@/assets/products-center/E-Motion products.png', import.meta.url).href
  },
  {
    id: 5,
    titleKey: 'home.products.machineParts',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"/></svg>',
    image: new URL('@/assets/products-center/Machine Parts.png', import.meta.url).href
  },
  {
    id: 6,
    titleKey: 'home.products.solutions',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5"/></svg>',
    image: new URL('@/assets/products-center/Customerized Solutions.png', import.meta.url).href
  }
])
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@300;400;500;600;700&family=Hind:wght@300;400;500;600;700&display=swap');

.product-showcase {
  padding: 90px 0;
  background-color: #f5f6f8;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 50px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}

.product-card {
  position: relative;
  height: 340px;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  background-size: cover;
  background-position: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.product-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
}

.card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.75));
  transition: all 0.4s ease;
}

.product-card:hover .card-overlay {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.85));
}

.card-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 35px;
}

.product-icon {
  margin-bottom: 24px;
  opacity: 0.95;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.product-card:hover .product-icon {
  transform: scale(1.15) translateY(-5px);
}

.product-title {
  color: white;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.4);
  line-height: 1.4;
  font-family: 'Bitter', serif;
  letter-spacing: 0.2px;
  transition: transform 0.3s;
}

.product-card:hover .product-title {
  transform: translateY(-3px);
}

@media (max-width: 1024px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
  }

  .container {
    padding: 0 30px;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  .product-showcase {
    padding: 70px 0;
  }

  .products-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .product-card {
    height: 300px;
  }

  .product-title {
    font-size: 22px;
  }
}
</style>
